<template>
  <teleport to="body">
    <div
      v-if="show"
      class="create-store-modal-shell"
      @click.self="closeModal"
    >
      <aside class="create-store-modal-panel" role="dialog" aria-modal="true" aria-labelledby="create-store-title">
        <div class="create-store-modal-header">
          <div class="create-store-modal-icon">
            <hs-icon name="shopping-bag" :size="22" />
          </div>
          <div class="min-w-0">
            <p class="create-store-modal-eyebrow">Create workspace</p>
            <h2 id="create-store-title">Create New Store</h2>
            <p class="create-store-modal-subtitle">
              Configure store identity, currency, timezone, and payment references.
            </p>
          </div>
          <hs-button class="create-store-modal-close" variant="secondary" icon="x" icon-only aria-label="Close panel"
            @click="closeModal" />
        </div>

        <form class="create-store-form" @submit.prevent="handleCreate">
      <div class="create-store-form__body">
        <div class="create-store-form__intro">
          <div class="create-store-form__intro-icon">
            <hs-icon name="shopping-bag" :size="20" />
          </div>
          <div>
            <p class="create-store-form__intro-title">Set up a new workspace</p>
            <p class="create-store-form__intro-copy">
              Create a store to manage inventory, sales, products, and business settings in one place.
            </p>
          </div>
        </div>

        <!-- Store Name -->
        <div class="create-store-field">
          <label
            for="store-name"
            class="create-store-label"
          >
            Store Name <span class="text-red-500">*</span>
          </label>
          <input
            id="store-name"
            v-model="formData.name"
            type="text"
            maxlength="64"
            placeholder="e.g., Downtown Shop"
            class="create-store-input"
            :class="{
              'is-invalid':
                errors.name,
            }"
            required
            @input="clearError('name')"
            @blur="validateField('name')"
          />
          <p v-if="errors.name" class="mt-1 text-sm text-red-600">
            {{ errors.name }}
          </p>
          <p v-else class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Maximum 64 characters
          </p>
        </div>

        <!-- Currency (required) -->
        <div v-click-outside="closeCurrencyDropdown" class="create-store-field">
          <label
            for="store-currency"
            class="create-store-label"
          >
            Currency <span class="text-red-500">*</span>
          </label>
          <div class="relative">
            <input
              id="store-currency"
              v-model="currencySearch"
              type="text"
              placeholder="Search currency (e.g., RWF, Dollar)"
              class="create-store-input create-store-input--dropdown"
              :class="{
                'is-invalid':
                  errors.currency,
                'is-valid':
                  !errors.currency,
              }"
              @focus="openCurrencyDropdown"
              @input="onCurrencyInput"
              @blur="validateField('currency')"
            />
            <button
              type="button"
              tabindex="-1"
              class="create-store-dropdown-toggle"
              @click="toggleCurrencyDropdown"
            >
              <svg
                class="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <ul
              v-if="showCurrencyDropdown && filteredCurrencies.length > 0"
              class="create-store-dropdown"
            >
              <li
                v-for="cur in filteredCurrencies"
                :key="cur.code"
                class="create-store-dropdown__option"
                :class="{
                  'is-selected':
                    formData.store_currency === cur.code,
                }"
                @mousedown.prevent="selectCurrency(cur)"
              >
                {{ cur.code }} - {{ cur.name }}
              </li>
            </ul>
            <p
              v-if="
                showCurrencyDropdown &&
                currencySearch &&
                filteredCurrencies.length === 0
              "
              class="create-store-dropdown create-store-dropdown--empty"
            >
              No currencies match "{{ currencySearch }}"
            </p>
          </div>
          <p v-if="errors.currency" class="mt-1 text-sm text-red-600">
            {{ errors.currency }}
          </p>
          <p
            v-else-if="formData.store_currency"
            class="mt-1 text-xs text-gray-500 dark:text-gray-400"
          >
            Selected: {{ formData.store_currency }}
          </p>
        </div>

        <!-- Timezone (required) -->
        <div v-click-outside="closeTimezoneDropdown" class="create-store-field">
          <label
            for="store-timezone"
            class="create-store-label"
          >
            Timezone <span class="text-red-500">*</span>
          </label>
          <div class="relative">
            <input
              id="store-timezone"
              v-model="timezoneSearch"
              type="text"
              :placeholder="'Search timezone (e.g., Africa/Kigali)'"
              class="create-store-input create-store-input--dropdown"
              :class="{
                'is-invalid':
                  errors.timezone,
                'is-valid':
                  !errors.timezone,
              }"
              @focus="openTimezoneDropdown"
              @input="onTimezoneInput"
              @blur="validateField('timezone')"
            />
            <button
              type="button"
              tabindex="-1"
              class="create-store-dropdown-toggle"
              @click="toggleTimezoneDropdown"
            >
              <svg
                class="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <ul
              v-if="showTimezoneDropdown && filteredTimezones.length > 0"
              class="create-store-dropdown"
            >
              <li
                v-for="tz in filteredTimezones"
                :key="tz.name"
                class="create-store-dropdown__option"
                :class="{
                  'is-selected':
                    formData.timezone_name === tz.name,
                }"
                @mousedown.prevent="selectTimezone(tz)"
              >
                {{ tz.label }}
              </li>
            </ul>
            <p
              v-if="
                showTimezoneDropdown &&
                timezoneSearch &&
                filteredTimezones.length === 0
              "
              class="create-store-dropdown create-store-dropdown--empty"
            >
              No timezones match "{{ timezoneSearch }}"
            </p>
          </div>
          <p v-if="errors.timezone" class="mt-1 text-sm text-red-600">
            {{ errors.timezone }}
          </p>
          <p
            v-else-if="formData.timezone_name"
            class="mt-1 text-xs text-gray-500 dark:text-gray-400"
          >
            Selected: {{ formData.timezone_name }}
          </p>
        </div>

        <div class="create-store-form__grid">
          <!-- Location (optional) -->
        <div class="create-store-field">
          <label
            for="store-location"
            class="create-store-label"
          >
            Location (optional)
          </label>
          <input
            id="store-location"
            v-model="formData.location"
            type="text"
            placeholder="Store address or location"
            class="create-store-input"
          />
        </div>

        <!-- Phone Number (optional) -->
        <div class="create-store-field">
          <label
            for="store-phone"
            class="create-store-label"
          >
            Phone Number (optional)
          </label>
          <input
            id="store-phone"
            v-model="formData.phone_number"
            type="tel"
            placeholder="Store phone number"
            class="create-store-input"
          />
        </div>

        <!-- Store Type (optional) -->
        <div class="create-store-field">
          <label
            for="store-type"
            class="create-store-label"
          >
            Store Type (optional)
          </label>
          <hs-select
            v-model="formData.store_type"
            :options="storeTypeOptions"
            placeholder="Select store type"
            icon="briefcase"
            :menu-z-index="5400"
            class="create-store-select"
          />
        </div>

        <!-- MTN Momo Code (optional) -->
        <div class="create-store-field">
          <label
            for="mtn-momo"
            class="create-store-label"
          >
            MTN Momo Code (optional)
          </label>
          <input
            id="mtn-momo"
            v-model="formData.mtn_momo_code"
            type="text"
            placeholder="MTN Mobile Money code"
            class="create-store-input"
          />
        </div>

        <!-- Airtel Money Code (optional) -->
        <div class="create-store-field">
          <label
            for="airtel-money"
            class="create-store-label"
          >
            Airtel Money Code (optional)
          </label>
          <input
            id="airtel-money"
            v-model="formData.airtel_money_code"
            type="text"
            placeholder="Airtel Money code"
            class="create-store-input"
          />
        </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="create-store-actions">
        <hs-button
          type="button"
          @click="closeModal"
          :disabled="isCreating"
          class="create-store-btn"
          variant="secondary"
        >
          Cancel
        </hs-button>
        <hs-button
          type="submit"
          :disabled="
            isCreating ||
            !formData.name.trim() ||
            !formData.store_currency ||
            !formData.timezone_name
          "
          class="create-store-btn"
          variant="primary"
          :loading="isCreating"
        >
          {{ isCreating ? "Creating..." : "Create Store" }}
        </hs-button>
      </div>
        </form>
      </aside>
    </div>
  </teleport>
</template>

<script>
import { getCurrencyFromPhone } from "@/utils/phoneCurrencyMap";
import allCurrencies from "@/utils/currencies";
import allTimezones from "@/utils/timezones";
import { HsButton, HsIcon, HsSelect } from "@/components/ui";

/**
 * Resolve the browser's IANA timezone to one from the curated list.
 * If the browser timezone isn't in the list, find the closest match by offset.
 */
function resolveBrowserTimezone() {
  const browserTz =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Africa/Kigali";
  // Exact match in curated list
  if (allTimezones.some((tz) => tz.name === browserTz)) return browserTz;
  // Fallback: match by browser UTC offset (JS getTimezoneOffset is inverted)
  const browserOffsetMinutes = -new Date().getTimezoneOffset();
  const offsetMatch = allTimezones.find(
    (tz) => tz.offset === browserOffsetMinutes,
  );
  if (offsetMatch) return offsetMatch.name;
  return "Africa/Kigali";
}

const clickOutside = {
  beforeMount(el, binding) {
    el._clickOutsideHandler = (event) => {
      if (!el.contains(event.target)) {
        binding.value(event);
      }
    };
    document.addEventListener("click", el._clickOutsideHandler);
  },
  unmounted(el) {
    document.removeEventListener("click", el._clickOutsideHandler);
  },
};

export default {
  name: "CreateStoreModal",
  components: {
    HsButton,
    HsIcon,
    HsSelect,
  },
  directives: {
    "click-outside": clickOutside,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "created"],
  data() {
    const userPhone =
      this.$store.getters.getAuthenticatedUser?.phone_number || "";
    const defaultCurrency = getCurrencyFromPhone(userPhone);
    return {
      formData: {
        name: "",
        location: "",
        phone_number: "",
        store_type: "",
        store_currency: defaultCurrency,
        timezone_name: resolveBrowserTimezone(),
        mtn_momo_code: "",
        airtel_money_code: "",
      },
      timezoneSearch: "",
      showTimezoneDropdown: false,
      currencySearch: "",
      showCurrencyDropdown: false,
      storeTypes: [
        { value: "RETAIL", label: "Retail" },
        { value: "SERVICE", label: "Service" },
        { value: "WHOLESALE", label: "Wholesale" },
        { value: "MANUFACTURING", label: "Manufacturing" },
        { value: "DISTRIBUTION", label: "Distribution" },
        { value: "E_COMMERCE", label: "E-Commerce" },
        { value: "OTHER", label: "Other" },
      ],

      errors: {
        name: "",
        currency: "",
        timezone: "",
      },
      isCreating: false,
    };
  },
  computed: {
    storeTypeOptions() {
      return [{ value: "", label: "Select store type" }, ...this.storeTypes];
    },
    filteredCurrencies() {
      if (!this.currencySearch) return allCurrencies;
      // If search matches the currently selected currency label, show all
      const selectedCur = allCurrencies.find(
        (c) => c.code === this.formData.store_currency,
      );
      if (
        selectedCur &&
        this.currencySearch === `${selectedCur.code} - ${selectedCur.name}`
      )
        return allCurrencies;
      const query = this.currencySearch.toLowerCase();
      return allCurrencies.filter(
        (c) =>
          c.code.toLowerCase().includes(query) ||
          c.name.toLowerCase().includes(query),
      );
    },
    filteredTimezones() {
      if (!this.timezoneSearch) return allTimezones;
      // If search text matches the currently selected timezone label, show all
      const selectedTz = allTimezones.find(
        (tz) => tz.name === this.formData.timezone_name,
      );
      if (selectedTz && this.timezoneSearch === selectedTz.label)
        return allTimezones;
      const query = this.timezoneSearch.toLowerCase();
      return allTimezones.filter(
        (tz) =>
          tz.label.toLowerCase().includes(query) ||
          tz.name.toLowerCase().includes(query),
      );
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // Reset form when modal opens
        const localTz = resolveBrowserTimezone();
        const userPhone =
          this.$store.getters.getAuthenticatedUser?.phone_number || "";
        this.formData = {
          name: "",
          location: "",
          phone_number: "",
          store_type: "",
          store_currency: getCurrencyFromPhone(userPhone),
          timezone_name: localTz,
          mtn_momo_code: "",
          airtel_money_code: "",
        };
        this.updateTimezoneSearchLabel();
        this.updateCurrencySearchLabel();
        this.showTimezoneDropdown = false;
        this.showCurrencyDropdown = false;
        this.errors = {
          name: "",
          currency: "",
          timezone: "",
        };
      }
    },
  },
  methods: {
    closeModal() {
      if (!this.isCreating) {
        this.$emit("close");
      }
    },
    clearError(field) {
      this.errors[field] = "";
    },
    validateField(field) {
      if (field === "name") {
        if (!this.formData.name || this.formData.name.trim() === "") {
          this.errors.name = "Store name is required";
        } else if (this.formData.name.length > 64) {
          this.errors.name = "Store name must be 64 characters or less";
        } else {
          this.errors.name = "";
        }
      }
      if (field === "currency") {
        if (!this.formData.store_currency) {
          this.errors.currency = "Currency is required";
        } else {
          this.errors.currency = "";
        }
      }
      if (field === "timezone") {
        if (!this.formData.timezone_name) {
          this.errors.timezone = "Timezone is required";
        } else {
          this.errors.timezone = "";
        }
      }
    },
    validateForm() {
      this.errors = { name: "", currency: "", timezone: "" };
      let isValid = true;

      if (!this.formData.name || this.formData.name.trim() === "") {
        this.errors.name = "Store name is required";
        isValid = false;
      } else if (this.formData.name.length > 64) {
        this.errors.name = "Store name must be 64 characters or less";
        isValid = false;
      }

      if (!this.formData.store_currency) {
        this.errors.currency = "Currency is required";
        isValid = false;
      }

      if (!this.formData.timezone_name) {
        this.errors.timezone =
          "Timezone is required. Please select a timezone from the dropdown.";
        isValid = false;
      } else {
        const isValidTz = allTimezones.some(
          (tz) => tz.name === this.formData.timezone_name,
        );
        if (!isValidTz) {
          this.errors.timezone =
            "Please select a valid timezone from the list.";
          isValid = false;
        }
      }

      return isValid;
    },
    async handleCreate() {
      // Prevent double submission
      if (this.isCreating) {
        return;
      }

      if (!this.validateForm()) {
        return;
      }

      this.isCreating = true;
      try {
        const storeData = {
          name: this.formData.name.trim(),
        };

        // Add optional fields only if they have values
        if (this.formData.location?.trim()) {
          storeData.location = this.formData.location.trim();
        }
        if (this.formData.phone_number?.trim()) {
          storeData.phone_number = this.formData.phone_number.trim();
        }
        if (this.formData.store_type) {
          storeData.store_type = this.formData.store_type;
        }
        if (this.formData.store_currency) {
          storeData.store_currency = this.formData.store_currency;
        }
        if (this.formData.mtn_momo_code?.trim()) {
          storeData.mtn_momo_code = this.formData.mtn_momo_code.trim();
        }
        if (this.formData.airtel_money_code?.trim()) {
          storeData.airtel_money_code = this.formData.airtel_money_code.trim();
        }
        if (this.formData.timezone_name) {
          storeData.timezone_name = this.formData.timezone_name;
        }

        console.log("Creating store with data:", storeData);
        const response = await this.$store.dispatch("createStore", storeData);
        console.log("Store creation response:", response);

        if (response && response.id) {
          this.$toast?.success?.(
            `Store "${response.name}" created successfully!`,
          );
          this.$emit("created", response);
          this.closeModal();
        } else {
          throw new Error("Failed to create store");
        }
      } catch (error) {
        console.error("Failed to create store:", error);

        // Handle specific error messages from the backend
        // Backend can return errors in multiple formats:
        // { error: { message: "..." } } or { message: "..." }
        const errorMessage =
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message ||
          "Failed to create store. Please try again.";
        this.$toast?.error?.(errorMessage);
      } finally {
        this.isCreating = false;
      }
    },
    selectTimezone(tz) {
      this.formData.timezone_name = tz.name;
      this.timezoneSearch = tz.label;
      this.showTimezoneDropdown = false;
      this.errors.timezone = "";
    },
    selectCurrency(cur) {
      this.formData.store_currency = cur.code;
      this.currencySearch = `${cur.code} - ${cur.name}`;
      this.showCurrencyDropdown = false;
      this.errors.currency = "";
    },
    openTimezoneDropdown() {
      this.showTimezoneDropdown = true;
    },
    closeTimezoneDropdown() {
      this.showTimezoneDropdown = false;
      // If user typed something but didn't select, revert to selected value
      if (this.formData.timezone_name) {
        this.updateTimezoneSearchLabel();
      }
    },
    toggleTimezoneDropdown() {
      this.showTimezoneDropdown = !this.showTimezoneDropdown;
    },
    openCurrencyDropdown() {
      this.showCurrencyDropdown = true;
    },
    closeCurrencyDropdown() {
      this.showCurrencyDropdown = false;
      if (this.formData.store_currency) {
        this.updateCurrencySearchLabel();
      }
    },
    toggleCurrencyDropdown() {
      this.showCurrencyDropdown = !this.showCurrencyDropdown;
    },
    onCurrencyInput() {
      this.showCurrencyDropdown = true;
      const match = allCurrencies.find(
        (c) =>
          `${c.code} - ${c.name}` === this.currencySearch ||
          c.code === this.currencySearch,
      );
      if (!match) {
        this.formData.store_currency = "";
      }
    },
    updateCurrencySearchLabel() {
      const match = allCurrencies.find(
        (c) => c.code === this.formData.store_currency,
      );
      this.currencySearch = match
        ? `${match.code} - ${match.name}`
        : this.formData.store_currency;
    },
    onTimezoneInput() {
      this.showTimezoneDropdown = true;
      // Clear the selected timezone when user modifies the search text
      // so they must pick from the list
      const match = allTimezones.find(
        (tz) =>
          tz.label === this.timezoneSearch || tz.name === this.timezoneSearch,
      );
      if (!match) {
        this.formData.timezone_name = "";
      }
    },
    updateTimezoneSearchLabel() {
      const match = allTimezones.find(
        (tz) => tz.name === this.formData.timezone_name,
      );
      this.timezoneSearch = match ? match.label : this.formData.timezone_name;
    },
  },
  mounted() {
    this.updateTimezoneSearchLabel();
    this.updateCurrencySearchLabel();
  },
};
</script>

<style scoped>
.create-store-modal-shell {
  align-items: stretch;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(8px);
  display: flex;
  inset: 0;
  justify-content: flex-end;
  padding: 18px;
  position: fixed;
  z-index: 5000;
}

.create-store-modal-panel {
  background:
    radial-gradient(circle at 90% 0%, color-mix(in srgb, var(--hs-primary, #3293ec) 14%, transparent), transparent 32%),
    var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 28px;
  box-shadow: 0 28px 90px rgba(15, 23, 42, 0.32);
  display: flex;
  flex-direction: column;
  max-width: 680px;
  overflow: hidden;
  width: min(680px, 100%);
}

.create-store-modal-header {
  align-items: flex-start;
  border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.18));
  display: grid;
  gap: 14px;
  grid-template-columns: auto 1fr auto;
  padding: 24px;
}

.create-store-modal-icon {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 13%, transparent);
  border-radius: 18px;
  color: var(--hs-primary, #3293ec);
  display: flex;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.create-store-modal-eyebrow {
  color: var(--hs-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  margin: 0 0 7px;
  text-transform: uppercase;
}

.create-store-modal-header h2 {
  color: var(--hs-text, #0f172a);
  font-size: 1.6rem;
  font-weight: 900;
  line-height: 1.15;
  margin: 0;
}

.create-store-modal-subtitle {
  color: var(--hs-muted, #64748b);
  font-size: 0.9rem;
  line-height: 1.55;
  margin-top: 8px;
}

.create-store-modal-close {
  align-items: center;
  background: color-mix(in srgb, var(--hs-muted, #64748b) 10%, transparent);
  border-radius: 14px;
  color: var(--hs-text, #0f172a);
  display: flex;
  height: 38px;
  justify-content: center;
  transition: background 0.2s ease, transform 0.2s ease;
  width: 38px;
}

.create-store-modal-close:hover {
  background: color-mix(in srgb, var(--hs-muted, #64748b) 16%, transparent);
  transform: translateY(-1px);
}

.create-store-form {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.create-store-form__body {
  display: grid;
  gap: 1rem;
  overflow-y: auto;
  padding: 24px;
}

.create-store-form__intro {
  align-items: flex-start;
  background:
    linear-gradient(135deg, color-mix(in srgb, var(--hs-primary, #3293ec) 9%, transparent), transparent),
    color-mix(in srgb, var(--hs-bg-soft, #f8fafc) 78%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-primary, #3293ec) 14%, var(--hs-border, #dbe4f0));
  border-radius: 22px;
  display: flex;
  gap: 0.85rem;
  padding: 1rem;
}

.create-store-form__intro-icon {
  align-items: center;
  background:
    radial-gradient(circle at 80% 0%, rgba(255, 255, 255, 0.38), transparent 32%),
    linear-gradient(135deg, var(--hs-primary, #3293ec), var(--hs-primary-strong, #1d4ed8));
  border-radius: 16px;
  box-shadow: 0 14px 32px color-mix(in srgb, var(--hs-primary, #3293ec) 22%, transparent);
  color: #fff;
  display: inline-flex;
  flex: 0 0 auto;
  height: 2.8rem;
  justify-content: center;
  width: 2.8rem;
}

.create-store-form__intro-title {
  color: var(--hs-text, #0f172a);
  font-size: 0.95rem;
  font-weight: 950;
  margin: 0 0 0.25rem;
}

.create-store-form__intro-copy {
  color: var(--hs-muted, #64748b);
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.55;
  margin: 0;
}

.create-store-form__grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.create-store-field {
  min-width: 0;
}

.create-store-label {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 0.79rem;
  font-weight: 900;
  margin-bottom: 0.42rem;
}

.create-store-input {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 88%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-border, #dbe4f0) 90%, transparent);
  border-radius: 16px;
  color: var(--hs-text, #0f172a);
  font-size: 0.88rem;
  font-weight: 760;
  min-height: 2.9rem;
  padding: 0 0.9rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  width: 100%;
}

.create-store-input--dropdown {
  padding-right: 2.65rem;
}

.create-store-input:focus {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 42%, var(--hs-border, #dbe4f0));
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent);
  outline: none;
}

.create-store-input.is-invalid {
  border-color: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
}

.create-store-dropdown-toggle {
  align-items: center;
  color: var(--hs-muted, #64748b);
  display: inline-flex;
  inset: 0 0 0 auto;
  justify-content: center;
  position: absolute;
  width: 2.6rem;
}

.create-store-dropdown {
  background:
    radial-gradient(circle at 90% 0%, color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent), transparent 34%),
    var(--hs-surface, #ffffff);
  border: 1px solid color-mix(in srgb, var(--hs-border, #dbe4f0) 88%, transparent);
  border-radius: 18px;
  box-shadow: 0 22px 54px rgba(15, 23, 42, 0.18);
  display: grid;
  gap: 0.3rem;
  left: 0;
  margin-top: 0.45rem;
  max-height: 13.5rem;
  overflow-y: auto;
  padding: 0.45rem;
  position: absolute;
  right: 0;
  z-index: 58;
}

.create-store-dropdown__option {
  border-radius: 13px;
  color: var(--hs-text, #0f172a);
  cursor: pointer;
  font-size: 0.84rem;
  font-weight: 800;
  padding: 0.65rem 0.75rem;
  transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease;
}

.create-store-dropdown__option:hover,
.create-store-dropdown__option.is-selected {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 11%, transparent);
  color: var(--hs-primary, #3293ec);
  transform: translateX(2px);
}

.create-store-dropdown--empty {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.84rem;
  font-weight: 800;
  padding: 0.8rem;
}

.create-store-select {
  min-width: 0;
  width: 100%;
}

.create-store-select :deep(.hs-custom-select) {
  min-width: 0;
  width: 100%;
}

.create-store-select :deep(.hs-custom-select__button) {
  border-radius: 16px;
  min-height: 2.9rem;
}

.create-store-actions {
  align-items: center;
  background: color-mix(in srgb, var(--hs-bg, #f9fafb) 66%, var(--hs-surface, #ffffff));
  border-top: 1px solid var(--hs-border, rgba(148, 163, 184, 0.18));
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 0;
  padding: 18px 24px;
}

.create-store-btn {
  align-items: center;
  border-radius: 16px;
  display: inline-flex;
  font-size: 0.88rem;
  font-weight: 950;
  gap: 0.5rem;
  justify-content: center;
  min-height: 2.8rem;
  padding: 0 1.05rem;
  transition: box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
}

.create-store-btn--ghost {
  background: var(--hs-surface, #ffffff);
  border: 1px solid color-mix(in srgb, var(--hs-border, #dbe4f0) 88%, transparent);
  color: var(--hs-muted, #64748b);
}

.create-store-btn--primary {
  background:
    radial-gradient(circle at 88% 0%, rgba(255, 255, 255, 0.34), transparent 30%),
    linear-gradient(135deg, var(--hs-primary, #3293ec), var(--hs-primary-strong, #1d4ed8));
  color: #fff;
  box-shadow: 0 14px 34px color-mix(in srgb, var(--hs-primary, #3293ec) 28%, transparent);
}

.create-store-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.create-store-btn:disabled {
  cursor: not-allowed;
  opacity: 0.56;
}

@media (max-width: 720px) {
  .create-store-modal-shell {
    align-items: flex-end;
    padding: 10px;
  }

  .create-store-modal-panel {
    border-radius: 24px;
    max-height: calc(100vh - 20px);
  }

  .create-store-modal-header {
    grid-template-columns: 1fr auto;
    padding: 18px;
  }

  .create-store-modal-icon {
    display: none;
  }

  .create-store-form__intro {
    border-radius: 18px;
  }

  .create-store-form__body,
  .create-store-actions {
    padding: 18px;
  }

  .create-store-form__grid {
    grid-template-columns: 1fr;
  }

  .create-store-actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .create-store-btn {
    width: 100%;
  }
}
</style>
